import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { AccountsReceivableSearchCondition } from '@/models/accounts-receivable-manager'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { DateRangeField } from '@/components/molecules/date-range-field'

type Props = {
  searchCondition: AccountsReceivableSearchCondition
  accountsReceivableMasters: Array<{ id: string; name: string }>
  onFilter: (val: AccountsReceivableSearchCondition) => void
}

export const AccountsReceivableFilterPopup: React.FC<Props> = props => {
  const { t } = useTranslation()
  const searchFormState = useForm<AccountsReceivableSearchCondition>()
  const watchCheckinFrom = useWatch({ control: searchFormState.control, name: 'checkinFrom' })
  const watchCheckinTo = useWatch({ control: searchFormState.control, name: 'checkinTo' })
  const watchCheckoutFrom = useWatch({ control: searchFormState.control, name: 'checkoutFrom' })
  const watchCheckoutTo = useWatch({ control: searchFormState.control, name: 'checkoutTo' })

  useEffect(() => {
    searchFormState.reset(props.searchCondition, { keepDefaultValues: true })
  }, [])

  return (
    <div css={popupStyle}>
      <div className="input-field-block">
        <div className="input-field">
          <div className="input-title">{t('Check-in date')}</div>
          <DateRangeField
            beginDate={watchCheckinFrom}
            endDate={watchCheckinTo}
            changeRangeDate={(beginDate: string, endDate: string) => {
              searchFormState.setValue('checkinFrom', beginDate)
              searchFormState.setValue('checkinTo', endDate)
            }}
          />
        </div>

        <div className="input-field">
          <div className="input-title">{t('Check-out date')}</div>
          <DateRangeField
            beginDate={watchCheckoutFrom}
            endDate={watchCheckoutTo}
            changeRangeDate={(beginDate: string, endDate: string) => {
              searchFormState.setValue('checkoutFrom', beginDate)
              searchFormState.setValue('checkoutTo', endDate)
            }}
          />
        </div>

        <Controller
          control={searchFormState.control}
          name="customerName"
          render={({ field: { onChange, value } }) => {
            // 予約詳細と同じく Option value は id（name だと同名マスタで検索が壊れる）
            // API の検索条件は名称のため、選択時に name へ変換する
            const selectedId = props.accountsReceivableMasters.find(master => master.name === value)?.id ?? ''

            return (
              <div className="input-field">
                <div className="input-title">{t('Accounts receivable')}</div>
                <AntSelect
                  showSearch
                  optionFilterProp="children"
                  filterOption={true}
                  style={{ width: '100%' }}
                  suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} />}
                  value={selectedId}
                  onChange={selectedValue => {
                    if (!selectedValue) {
                      onChange('')
                      return
                    }
                    const master = props.accountsReceivableMasters.find(item => item.id === selectedValue)
                    onChange(master?.name ?? '')
                  }}
                >
                  <AntSelect.Option value="">{t('All')}</AntSelect.Option>
                  {props.accountsReceivableMasters.map((master, index) => (
                    <AntSelect.Option key={index} value={master.id}>
                      {master.name}
                    </AntSelect.Option>
                  ))}
                </AntSelect>
              </div>
            )
          }}
        />

        <Controller
          control={searchFormState.control}
          name="salesType"
          render={({ field: { onChange, value } }) => (
            <div className="input-field">
              <div className="input-title">{t('Type')}</div>
              <Select customStyle={css({ paddingBottom: 0 })} value={value || ''} onChange={onChange}>
                <AntSelect.Option value="">{t('All')}</AntSelect.Option>
                <AntSelect.Option value="sales">{t('Sales')}</AntSelect.Option>
                <AntSelect.Option value="discount">{t('Discount')}</AntSelect.Option>
              </Select>
            </div>
          )}
        />

        <Controller
          control={searchFormState.control}
          name="reconciledStatus"
          render={({ field: { onChange, value } }) => (
            <div className="input-field">
              <div className="input-title">{t('Reconciliation status')}</div>
              <Select customStyle={css({ paddingBottom: 0 })} value={value || ''} onChange={onChange}>
                <AntSelect.Option value="">{t('All')}</AntSelect.Option>
                <AntSelect.Option value="reconciled">{t('Reconciled')}</AntSelect.Option>
                <AntSelect.Option value="unreconciled">{t('Unreconciled')}</AntSelect.Option>
              </Select>
            </div>
          )}
        />
      </div>
      <div className="footer-block">
        <Button buttonType={ButtonType.Secondary} height={32} width={80} marginRight={16} onClick={() => searchFormState.reset()}>
          {t('Reset')}
        </Button>
        <Button buttonType={ButtonType.Primary} height={32} width={80} onClick={searchFormState.handleSubmit(props.onFilter)}>
          {t('Apply')}
        </Button>
      </div>
    </div>
  )
}

const popupStyle = css({
  width: 'max-content',
  padding: 16,
  lineHeight: 1,
  '.input-field-block': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    '@media(max-width: 1080px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    gap: '1rem',
  },
  '.footer-block': {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  '.input-field': {
    marginBottom: 16,
    '.input-title': {
      display: 'flex',
      fontSize: 12,
      fontWeight: 'bold',
      letterSpacing: 0.6,
      paddingBottom: 12,
    },
  },
})
